import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Mail, MessageSquare, Smartphone, Send, Eye, Copy, Edit, Trash2, BarChart3, Zap, X } from 'lucide-react';

const CommunicationsTemplates = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Mock data pour les templates
  const templates = [
    {
      id: 1,
      name: "Confirmation de Réservation",
      description: "Template automatique envoyé après confirmation",
      channels: ["Email", "WhatsApp"],
      triggers: ["Réservation confirmée"],
      status: "active",
      stats: {
        sent: 245,
        opened: 89,
        clicked: 23
      },
      content: {
        subject: "Confirmation de votre réservation PRIVIA",
        body: "Cher {{client_name}}, nous avons le plaisir de confirmer votre réservation..."
      },
      category: "booking"
    },
    {
      id: 2,
      name: "Rappel Check-in",
      description: "Rappel 24h avant l'arrivée",
      channels: ["Email", "SMS"],
      triggers: ["24h avant arrivée"],
      status: "active",
      stats: {
        sent: 156,
        opened: 98,
        clicked: 45
      },
      content: {
        subject: "Rappel: Votre arrivée demain chez PRIVIA",
        body: "Bonjour {{client_name}}, nous avons hâte de vous accueillir demain..."
      },
      category: "checkin"
    },
    {
      id: 3,
      name: "Demande d'Avis",
      description: "Sollicitation d'avis après le séjour",
      channels: ["Email"],
      triggers: ["3 jours après départ"],
      status: "active",
      stats: {
        sent: 189,
        opened: 67,
        clicked: 34
      },
      content: {
        subject: "Comment s'est passé votre séjour ?",
        body: "Cher {{client_name}}, nous espérons que vous avez passé un merveilleux séjour..."
      },
      category: "feedback"
    },
    {
      id: 4,
      name: "Offre Spéciale VIP",
      description: "Offres exclusives pour clients VIP",
      channels: ["Email", "WhatsApp"],
      triggers: ["Manuel", "Segment VIP"],
      status: "draft",
      stats: {
        sent: 67,
        opened: 45,
        clicked: 18
      },
      content: {
        subject: "Offre exclusive PRIVIA - Réservé à nos VIP",
        body: "Cher {{client_name}}, en tant que client privilégié..."
      },
      category: "marketing"
    }
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'booking': return 'bg-blue-100 text-blue-800';
      case 'checkin': return 'bg-green-100 text-green-800';
      case 'feedback': return 'bg-purple-100 text-purple-800';
      case 'marketing': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'Email': return <Mail size={16} />;
      case 'WhatsApp': return <MessageSquare size={16} />;
      case 'SMS': return <Smartphone size={16} />;
      default: return <Mail size={16} />;
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesFilter = activeFilter === 'all' || template.category === activeFilter;
    const matchesChannel = channelFilter === 'all' || template.channels.includes(channelFilter);
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesChannel && matchesSearch;
  });

  const TemplateModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">
              {selectedTemplate ? 'Modifier le Template' : 'Nouveau Template'}
            </h3>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du Template
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="Ex: Confirmation de réservation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  rows="3"
                  placeholder="Description du template"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Canaux de Communication
                </label>
                <div className="space-y-2">
                  {['Email', 'WhatsApp', 'SMS'].map((channel) => (
                    <label key={channel} className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" />
                      <span className="text-sm text-gray-700">{channel}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                  <option value="booking">Réservation</option>
                  <option value="checkin">Check-in</option>
                  <option value="feedback">Feedback</option>
                  <option value="marketing">Marketing</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Déclencheurs
                </label>
                <div className="space-y-2">
                  {[
                    'Réservation confirmée',
                    '24h avant arrivée',
                    '3 jours après départ',
                    'Manuel'
                  ].map((trigger) => (
                    <label key={trigger} className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" />
                      <span className="text-sm text-gray-700">{trigger}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Contenu */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sujet (Email)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="Sujet de l'email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu du Message
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  rows="8"
                  placeholder="Tapez votre message ici..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variables Disponibles
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    '{{client_name}}',
                    '{{property_name}}',
                    '{{checkin_date}}',
                    '{{checkout_date}}',
                    '{{booking_reference}}'
                  ].map((variable) => (
                    <button
                      key={variable}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                      onClick={() => {
                        // Logique pour insérer la variable
                      }}
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Aperçu</h4>
                <div className="text-sm text-yellow-700">
                  <p>Sujet: Confirmation de votre réservation PRIVIA</p>
                  <p className="mt-2">Cher John Doe, nous avons le plaisir de confirmer votre réservation à Villa Azure du 15 au 22 septembre 2024...</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                // Logique de sauvegarde
                setShowModal(false);
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="communications-templates">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Templates de Communication</h1>
            <p className="text-gray-600 mt-1">Gérez vos templates automatisés pour tous les canaux de communication</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Send size={18} />
              <span>Tester Envoi</span>
            </button>
            <button
              onClick={() => {
                setSelectedTemplate(null);
                setShowModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <Plus size={18} />
              <span>Nouveau Template</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher un template..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <select 
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes catégories</option>
              <option value="booking">Réservation</option>
              <option value="checkin">Check-in</option>
              <option value="feedback">Feedback</option>
              <option value="marketing">Marketing</option>
            </select>

            <select 
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous canaux</option>
              <option value="Email">Email</option>
              <option value="WhatsApp">WhatsApp</option>
              <option value="SMS">SMS</option>
            </select>
          </div>
        </div>

        {/* Grille des Templates */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div key={template.id} className="template-card bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-2">{template.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  
                  <div className="flex items-center space-x-2 mb-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(template.category)}`}>
                      {template.category}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      template.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.status === 'active' ? 'Actif' : 'Brouillon'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => {
                      setSelectedTemplate(template);
                      setShowModal(true);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Edit size={16} />
                  </button>
                  <button className="text-gray-400 hover:text-gray-600">
                    <Copy size={16} />
                  </button>
                  <button className="text-gray-400 hover:text-red-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Canaux */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-600 mb-2">CANAUX</p>
                <div className="flex items-center space-x-2">
                  {template.channels.map((channel) => (
                    <div key={channel} className="flex items-center space-x-1 bg-gray-100 px-2 py-1 rounded text-xs">
                      {getChannelIcon(channel)}
                      <span>{channel}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Déclencheurs */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-600 mb-2">DÉCLENCHEURS</p>
                <div className="flex flex-wrap gap-1">
                  {template.triggers.map((trigger) => (
                    <span key={trigger} className="inline-block bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                      {trigger}
                    </span>
                  ))}
                </div>
              </div>

              {/* Statistiques */}
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-semibold text-gray-800">{template.stats.sent}</div>
                    <div className="text-xs text-gray-600">Envoyés</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-blue-600">{template.stats.opened}</div>
                    <div className="text-xs text-gray-600">Ouverts</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-green-600">{template.stats.clicked}</div>
                    <div className="text-xs text-gray-600">Clics</div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800">
                  <Eye size={14} />
                  <span>Prévisualiser</span>
                </button>
                <button className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800">
                  <BarChart3 size={14} />
                  <span>Analytics</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bouton flottant */}
        <button
          onClick={() => {
            setSelectedTemplate(null);
            setShowModal(true);
          }}
          className="fixed bottom-6 right-6 w-14 h-14 bg-yellow-600 text-white rounded-full shadow-lg hover:bg-yellow-700 flex items-center justify-center"
        >
          <Plus size={24} />
        </button>
      </div>

      <TemplateModal />
    </div>
  );
};

export default CommunicationsTemplates;